import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import type { DBLocation, DBLocationOverview, DBLocationType } from "@/lib/db-types";

const BUCKET = "location-media";
const SIGNED_URL_TTL = 3600;

const OVERVIEW_COLUMNS =
  "id, slug, name, type_id, type_label, indoor, address, postal_code, postal_place, municipality_number, municipality_name, district, country_code, lat, lon, tags, description, archived_at, legacy_id, created_at, updated_at, image_count, cover_path, project_count, used_count";

/** Typene i lista og skjemaet, også inaktive (de vises på eldre lokasjoner). */
export function useLocationTypes(enabled = true) {
  return useQuery({
    queryKey: ["location-types"],
    enabled,
    queryFn: async (): Promise<DBLocationType[]> => {
      const { data, error } = await supabase
        .from("location_types")
        .select("id, label, indoor, sort_order, is_active")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as DBLocationType[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Hele banken via locations_overview_v, også arkiverte (filtreres i klienten). */
export function useLocationsOverview(enabled = true) {
  return useQuery({
    queryKey: ["locations"],
    enabled,
    queryFn: () => fetchAllPaginated<DBLocationOverview>("locations_overview_v", OVERVIEW_COLUMNS),
  });
}

export function useLocation(slug: string | undefined) {
  return useQuery({
    queryKey: ["location-detail", slug],
    enabled: !!slug,
    queryFn: async (): Promise<DBLocation | null> => {
      const { data, error } = await supabase.from("locations").select("*").eq("slug", slug!).maybeSingle();
      if (error) throw error;
      return data as DBLocation | null;
    },
  });
}

export interface LocationHistoryEntry {
  id: string;
  event_type: string;
  payload: Record<string, unknown> | null;
  created_at: string;
  users: { full_name: string } | null;
}

/** Historikk fra event_log (audit-triggerne og importen). */
export function useLocationHistory(locationId: string | undefined) {
  return useQuery({
    queryKey: ["location-history", locationId],
    enabled: !!locationId,
    queryFn: async (): Promise<LocationHistoryEntry[]> => {
      const { data, error } = await supabase
        .from("event_log")
        .select("id, event_type, payload, created_at, users:user_id(full_name)")
        .eq("entity_type", "locations")
        .eq("entity_id", locationId!)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as LocationHistoryEntry[];
    },
  });
}

export interface NearbyLocation {
  id: string;
  slug: string;
  name: string;
  distance_m: number;
}

/** Lokasjoner innenfor 150 m, for duplikatvarsel i skjemaet. */
export function useNearbyLocations(lat: number | null, lon: number | null, excludeId?: string) {
  return useQuery({
    queryKey: ["locations", "near", lat, lon, excludeId ?? null],
    enabled: lat !== null && lon !== null,
    queryFn: async (): Promise<NearbyLocation[]> => {
      const { data, error } = await supabase.rpc("locations_near", {
        p_lat: lat,
        p_lon: lon,
        p_radius_m: 150,
        p_exclude: excludeId ?? null,
      });
      if (error) throw error;
      return (data ?? []) as NearbyLocation[];
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Signerte lenker for en liste stier i bøtta (privat). Én runde per liste;
 * lenkene varer en time og caches i 50 minutter.
 */
export function useSignedImageUrls(paths: string[]) {
  const unique = [...new Set(paths.filter(Boolean))].sort();
  return useQuery({
    queryKey: ["location-images", "signed", unique],
    enabled: unique.length > 0,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(unique, SIGNED_URL_TTL);
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const s of data ?? []) if (s.path && s.signedUrl) map[s.path] = s.signedUrl;
      return map;
    },
    staleTime: 50 * 60 * 1000,
  });
}
